/**
 * The runner's own state (pedagogy spec §6.3.4): which of the day's steps are done, skipped or
 * under way. Pure helpers plus a guarded `localStorage` copy (`mf.today.session`), so a refresh
 * of the runner or a trip to Home and back keeps the timeline. The practice session and the card
 * review keep their own state; this only remembers how far the day's session got.
 *
 * A step is finished when its page sends the student back with `?done=<step>` (the hand-offs
 * below). The state is thrown away when the day changes: it belongs to one `dayKey`.
 */
import { STEP_IDS, type StepId } from "./types";

import { belongsToOwner, getStorageOwner } from "@client/lib/storageOwner";

export const RUNNER_KEY = "mf.today.session";
/** The runner's route; the only place a hand-off may return to besides the flashcards. */
export const TODAY_PATH = "/practice/today";

export type StepStatus = "pending" | "started" | "done" | "skipped";

export interface StepRecord {
  status: StepStatus;
  /** How many cards, re-tests or problems the step held when it started (shown once it is over). */
  count?: number;
}

export interface TodayRunnerState {
  version: 1;
  /** The local day this state belongs to (the plan's `dayKey`). */
  dayKey: string;
  startedAt: number;
  steps: Partial<Record<StepId, StepRecord>>;
  /** The topic of the new-topic step once it started, so its title survives a change of plan. */
  newTopicId?: string;
}

export function newRunner(dayKey: string, now: number = Date.now()): TodayRunnerState {
  return { version: 1, dayKey, startedAt: now, steps: {} };
}

/** The status of a step; one that was never touched is pending. */
export function statusOf(state: TodayRunnerState | null, id: StepId): StepStatus {
  return state?.steps[id]?.status ?? "pending";
}

function withStep(state: TodayRunnerState, id: StepId, record: StepRecord): TodayRunnerState {
  return { ...state, steps: { ...state.steps, [id]: record } };
}

/** The student is on the step (or came back to it): `count` is what it holds right now. */
export function startStep(
  state: TodayRunnerState,
  id: StepId,
  info: { count: number; topicId?: string },
): TodayRunnerState {
  const next = withStep(state, id, { status: "started", count: info.count });
  return id === "new" && info.topicId ? { ...next, newTopicId: info.topicId } : next;
}

/** A finished step keeps the count it started with. */
export function finishStep(state: TodayRunnerState, id: StepId): TodayRunnerState {
  const count = state.steps[id]?.count;
  return withStep(state, id, { status: "done", ...(count !== undefined ? { count } : {}) });
}

export function skipStep(state: TodayRunnerState, id: StepId): TodayRunnerState {
  const count = state.steps[id]?.count;
  return withStep(state, id, { status: "skipped", ...(count !== undefined ? { count } : {}) });
}

/** "Gör ändå": a skipped step goes back to what it was before the student left it out. */
export function reopenStep(state: TodayRunnerState, id: StepId): TodayRunnerState {
  const { [id]: _removed, ...rest } = state.steps;
  return { ...state, steps: rest };
}

/** "Sluta här för idag": every step of `ids` that is not done is skipped. */
export function stopForToday(state: TodayRunnerState, ids: readonly StepId[]): TodayRunnerState {
  return ids.reduce((acc, id) => (statusOf(acc, id) === "done" ? acc : skipStep(acc, id)), state);
}

/** The `returnTo` a step hands to its page; the runner reads `?done=` once and strips it. */
export function returnToFor(id: StepId): string {
  return `${TODAY_PATH}?done=${id}`;
}

/** The step a `?done=` parameter names, or null for anything else. */
export function doneStepOf(params: URLSearchParams): StepId | null {
  const raw = params.get("done");
  return STEP_IDS.find((id) => id === raw) ?? null;
}

/** True for a `returnTo` that leads back to the runner (checked before the summary navigates). */
export function isTodayReturn(path: string | undefined): path is string {
  return typeof path === "string" && /^\/practice\/today(\?|#|$)/.test(path);
}

const STATUSES: readonly string[] = ["pending", "started", "done", "skipped"];

function readState(raw: unknown): TodayRunnerState | null {
  if (typeof raw !== "object" || raw === null) return null;
  const value = raw as Record<string, unknown>;
  if (value.version !== 1 || typeof value.dayKey !== "string") return null;
  if (typeof value.startedAt !== "number") return null;
  const stored =
    typeof value.steps === "object" && value.steps !== null
      ? (value.steps as Record<string, unknown>)
      : {};
  const steps: Partial<Record<StepId, StepRecord>> = {};
  for (const id of STEP_IDS) {
    const entry = stored[id];
    if (typeof entry !== "object" || entry === null) continue;
    const { status, count } = entry as Record<string, unknown>;
    if (typeof status !== "string" || !STATUSES.includes(status)) continue;
    steps[id] = {
      status: status as StepStatus,
      ...(typeof count === "number" && Number.isFinite(count) ? { count } : {}),
    };
  }
  return {
    version: 1,
    dayKey: value.dayKey,
    startedAt: value.startedAt,
    steps,
    ...(typeof value.newTopicId === "string" ? { newTopicId: value.newTopicId } : {}),
  };
}

export function saveRunner(state: TodayRunnerState): void {
  const owner = getStorageOwner();
  try {
    // No known owner: the durable copy would be unattributable, so only this tab remembers it.
    if (owner === null) window.sessionStorage.setItem(RUNNER_KEY, JSON.stringify(state));
    else window.localStorage.setItem(RUNNER_KEY, JSON.stringify({ ...state, userId: owner }));
  } catch {
    /* storage unavailable: the timeline holds for this page only */
  }
}

export function clearRunner(): void {
  try {
    window.localStorage.removeItem(RUNNER_KEY);
  } catch {
    /* ignore */
  }
  try {
    window.sessionStorage.removeItem(RUNNER_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * The stored state, or null when there is none, it is unreadable, or it belongs to another day
 * (then it is dropped). Pass the plan's `dayKey` once it is known.
 */
export function loadRunner(dayKey?: string): TodayRunnerState | null {
  try {
    // localStorage since P0-2 (a new tab or app restart keeps a skipped step); an older tab's
    // sessionStorage copy is still read.
    const durable = window.localStorage.getItem(RUNNER_KEY);
    const raw = durable ?? window.sessionStorage.getItem(RUNNER_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    const stamped = (parsed as { userId?: unknown } | null)?.userId ?? null;
    // Another account's state (a cookie expired, then someone else signed in): not theirs to see.
    // The durable copy must carry the owner's stamp; a tab's own copy may lack one.
    if (!belongsToOwner(stamped, durable !== null)) {
      clearRunner();
      return null;
    }
    const state = readState(parsed);
    if (!state) return null;
    if (dayKey !== undefined && state.dayKey !== dayKey) {
      clearRunner();
      return null;
    }
    return state;
  } catch {
    return null;
  }
}
